import { EventNames, isMobile } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';

import { getTranslation, keys } from '../../../Resources/Translations';
import { AlertRibbon } from '../../Blocks/AlertRibbon/AlertRibbon';
import { Ribbon } from '../../Blocks/Ribbon/Ribbon';
import { EnvironmentLabel } from '../../Components/EnvironmentLabel/EnvironmentLabel';
import { RequiresLoginModal } from '../../Customers/Favourites/Components/RequiresLoginModal/RequiresLoginModal';
import { favourites } from '../../Customers/Favourites/Favourites';
import { CookieAccessor } from '../../Customers/UserProfile/Components/CookiesConsent/CookieAccessor';
import { CartAccessor } from '../../Purchase/Cart/Components/CartAccessor';
import { CartMergedModal, showCartMergedModal } from '../../Purchase/Cart/Components/CartMergedModal';
import { UserDetailsViewModel } from '../../Purchase/Cart/PersonalizedCart/UserDetailsViewModel.csharp';
import { CartViewModel } from '../../Purchase/Cart/ViewModels/CartViewModel.csharp';
import { showRxSessionExpiredModal } from '../../Purchase/Rx/Modals/RxSessionExpiredModal';
import { RxSessionModals } from '../../Purchase/Rx/Modals/RxSessionModals';
import { del, post } from '../../ReactComponents/API';
import { CooledProductsConflictModal } from '../../ReactComponents/CooledProductsConflict/CooledProductsConflictModal';
import { isTablet } from '../../ReactComponents/IsTablet';
import { LoginModal } from '../../ReactComponents/Modal/LoginModal';
import { PhilExpectedDeliveryDateViewWrapper } from '../../ReactComponents/Modal/PhilExpectedDeliveryDate/PhilExpectedDeliveryDateViewWrapper';
import { NewMessageSnackbar } from '../../ReactComponents/Snackbar/NewMessageSnackbar/NewMessageSnackbar';
import { SessionStorageAccessor } from '../../StorageAccessor/SessionStorageAccessor';
import { Navigation } from '../Navigation/Navigation';
import { Search } from '../Search/Search';
import { FatalErrorModal } from './FatalErrorModal';
import { HeaderActions } from './HeaderActions';
import { HeaderButtons } from './HeaderButtons';
import { HeaderMenuButton } from './HeaderMenuButton/HeaderMenuButton';
import { HeaderProps } from './HeaderProps.csharp';
import { HeaderSearchButton } from './HeaderSearchButton/HeaderSearchButton';
import { HeaderVariant } from './HeaderVariant.csharp';

interface HeaderState {
    readonly isSticky: boolean;
    readonly isActive: boolean;
    readonly isScrollLockActive: boolean;
    readonly isSearchOpen: boolean;
    readonly isSearchActive: boolean;
    readonly isActionMenuOpen: boolean;
    readonly isRxMenuOpen: boolean;
    readonly isInPhysicalStoreMode: boolean;
    readonly userDetails?: UserDetailsViewModel;
    readonly isMobile: boolean;
    readonly isTablet: boolean;
    readonly isAlertRibbonVisible: boolean;
    readonly shouldBeAlertRibbonHidden: boolean;
    readonly isNavigationOpen: boolean;
    readonly isSearchOverlayShown: boolean;
    readonly isClient: boolean;
}

function patchPhilLinks(philDomain: string) {
    const anchors = document.querySelectorAll('a');

    if (anchors) {
        const anchorsArray = Array.prototype.slice.apply(anchors) as HTMLAnchorElement[];

        anchorsArray
            .filter((a) => a.href?.startsWith(philDomain))
            .forEach((a) => {
                const url = new URL(a.href);
                url.searchParams.set('ecomauthenticated', 'true');

                a.href = url.toString();
            });
    }
}

export default class Header extends React.Component<HeaderProps, HeaderState> {
    constructor(props: HeaderProps) {
        super(props);

        this.state = {
            isSticky: false,
            isActive: false,
            isScrollLockActive: false,
            isSearchOpen: false,
            isSearchActive: false,
            isActionMenuOpen: false,
            isRxMenuOpen: false,
            isInPhysicalStoreMode: props.model.isInPhysicalStoreMode,
            isMobile: isMobile(),
            isTablet: isTablet(),
            isAlertRibbonVisible: !props.model.alertRibbonProps?.alertRibbon?.isDismissed && !!props.model.alertRibbonProps?.alertRibbon?.text,
            shouldBeAlertRibbonHidden: false,
            isNavigationOpen: false,
            isSearchOverlayShown: false,
            isClient: false
        };
    }

    lastOffsetTop = 0;
    blockHandleScroll = false;

    searchInputWrapperRef = React.createRef<HTMLDivElement>();
    headerRef = React.createRef<HTMLDivElement>();
    ribbonRef = React.createRef<HTMLDivElement>();
    updateCartTimeoutRef = React.createRef<ReturnType<typeof setTimeout>>();
    blockHandleScrollTimeoutRef = React.createRef<ReturnType<typeof setTimeout>>();

    componentDidMount() {
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener(EventNames.CloseNavigation, this.closeNavigation);
        window.addEventListener(EventNames.CloseSearch, this.closeSearch);
        window.addEventListener(EventNames.IframeFocused, this.closeAllMenuItems);
        window.addEventListener(EventNames.ScrollLockChange, this.handleScrollLock);
        window.addEventListener(EventNames.HideHeader, this.hideHeader);
        window.addEventListener(EventNames.RequestForUpdatingCart, this.showHeaderBeforeCartUpdate);
        window.addEventListener(EventNames.HideAlertRibbon, this.hideAlertRibbon);
        window.addEventListener('click', this.handleClickOutside);
        window.addEventListener('resize', this.handleResize);

        this.setState({ isMobile: isMobile(), isTablet: isTablet(), isClient: true });

        CartAccessor.getPersonalizedCart(this.props.model.links.cart)
            .then((personalizedCart) => {
                this.showUpdatedCart(personalizedCart.cart);
                this.setUserDetails(personalizedCart.cart, personalizedCart.user);
                window.dispatchEvent(
                    new CustomEvent(EventNames.UserDetailsLoaded, {
                        detail: { user: personalizedCart.user }
                    })
                );

                favourites.validateFavourites(this.props.model.links.favouritesApi, personalizedCart.user);
            })
            .catch((err) => console.log(err));
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener(EventNames.CloseNavigation, this.closeNavigation);
        window.removeEventListener(EventNames.CloseSearch, this.closeSearch);
        window.removeEventListener(EventNames.IframeFocused, this.closeAllMenuItems);
        window.removeEventListener(EventNames.ScrollLockChange, this.handleScrollLock);
        window.removeEventListener(EventNames.HideHeader, this.hideHeader);
        window.removeEventListener(EventNames.RequestForUpdatingCart, this.showHeaderBeforeCartUpdate);
        window.removeEventListener(EventNames.HideAlertRibbon, this.hideAlertRibbon);
        window.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('resize', this.handleResize);
        if (this.updateCartTimeoutRef.current) {
            clearTimeout(this.updateCartTimeoutRef.current);
        }
        if (this.blockHandleScrollTimeoutRef.current) {
            clearTimeout(this.blockHandleScrollTimeoutRef.current);
        }
    }

    render() {
        const {
            state: { isSticky, isSearchActive, isActionMenuOpen, isRxMenuOpen, isInPhysicalStoreMode, isNavigationOpen, isMobile, isTablet, isClient }
        } = this;

        const {
            websiteVersion,
            profile,
            model: {
                displayCart,
                searchPhrase,
                navigation,
                variant,
                searchPlaceholder,
                environmentLabel,
                showEnvironmentLabel,
                alertRibbonProps,
                ribbon,
                links,
                headerLabels,
                leadInText
            }
        } = this.props;
        const isOnlyLogoVariant = variant === HeaderVariant.LogoOnly;
        const isHomePageVariant = variant === HeaderVariant.HomePage;
        const isSearchPageVariant = variant === HeaderVariant.Search;
        const isSearchInitiallyOpen = isClient
            ? isHomePageVariant || isSearchPageVariant || (!isTablet && !isMobile)
            : isHomePageVariant || isSearchPageVariant;
        const isSearchOpen = isClient
            ? this.state.isSearchOpen || (isSearchInitiallyOpen && !isSticky) || (isSearchInitiallyOpen && isSticky && !isMobile && !isTablet)
            : this.state.isSearchOpen || (isSearchInitiallyOpen && !isSticky) || (isSearchInitiallyOpen && isSticky);
        const isShowSearchButtonVisible = (isSticky || !isSearchInitiallyOpen) && !isSearchPageVariant;

        return (
            <>
                <NewMessageSnackbar inboxUrl={links.inboxUrl} philUrl={this.props.philDomain} />
                <LoginModal loginUrl={links.login} />
                <RequiresLoginModal loginUrl={links.login} registerUrl={links.register} />
                <CartMergedModal acknowledgeMergeUrl={links.acknowledgeMerge} />
                <RxSessionModals
                    cartPageUrl={links.cartSummary}
                    acknowledgeRxsRemovedUrl={links.acknowledgeRxsRemoved}
                    acknowledgeRxsWillRemoveUrl={links.acknowledgeRxsWillRemove}
                    checkConditionsForRxExpiringModal={links.checkConditionsForRxExpiringModal}
                    checkConditionsForRxExpiredModal={links.checkConditionsForRxExpiredModal}
                    bankIdLoginUrl={links.secureLoginLandingPage}
                    bankIdLogoutUrl={links.bankIdLogout}
                    ecomLogoutUrl={links.logout}
                    rxSessionModalConfiguration={this.props.model.rxSessionModalConfiguration}
                    minNotificationThreshold={this.props.model.rxSessionModalMinNotificationThreshold}
                    maxNotificationThreshold={this.props.model.rxSessionModalMaxNotificationThreshold}
                    requiresSecureLogin={this.props.model.requiresSecureLogin}
                />
                <FatalErrorModal />
                <CooledProductsConflictModal pictureProfile={profile} removeItemsUrl={this.props.model.links.cartRemoveItems} />
                <PhilExpectedDeliveryDateViewWrapper {...this.props.philExpectedDeliveryDateProps} />
                {!this.state.shouldBeAlertRibbonHidden && alertRibbonProps?.alertRibbon?.text && (
                    <AlertRibbon
                        {...alertRibbonProps}
                        ref={this.ribbonRef}
                        onChange={(isVisible: boolean) => this.setState({ isAlertRibbonVisible: isVisible })}
                    />
                )}
                {!!ribbon.items?.length && <Ribbon items={ribbon.items} />}
                <header className={this.getHeaderClassNames()} ref={this.headerRef}>
                    <a className="Header__SkipToContent" href="#mainContent">
                        {getTranslation(keys['navigation.skipToContent'])}
                    </a>
                    <a className="Header__SkipToContent" href={links.cartSummary}>
                        {getTranslation(keys['navigation.goToCheckout'])}
                    </a>
                    <EnvironmentLabel showEnvironmentLabel={showEnvironmentLabel} environmentLabel={environmentLabel} />

                    {!isOnlyLogoVariant && (
                        <div className={this.getHeaderTopWrapperClassNames(isSearchInitiallyOpen)} key={isClient ? 'client' : 'server'}>
                            <div className={this.getHeaderTopClassNames(isMobile)}>
                                <div className="Header__TopContent">
                                    <div className="Header__LeftSide">
                                        <div className="Header__NavigationButtons">
                                            <HeaderMenuButton
                                                isVisible={isNavigationOpen}
                                                isSticky={isSticky}
                                                showButtonLabel={headerLabels.showLabels}
                                                buttonLabel={headerLabels.menu}
                                                toggleMenu={this.toggleNavigation}
                                            />
                                        </div>
                                    </div>

                                    <a href="/" className="Header__Logo">
                                        <img src={`/dist/assets/images/logo.svg?v=${websiteVersion}`} alt="Farmasiet Logo" width="230" height="32" />
                                    </a>

                                    <div className="Header__SearchWrapper">
                                        <Search
                                            ref={this.searchInputWrapperRef}
                                            isOpen={isSearchOpen}
                                            variant={variant}
                                            searchApiUrl={links.searchApi}
                                            epiFindClickTrackingUrl={links.epiFindClickTrackingUrl}
                                            experimentProductClickUrl={links.experimentProductClickUrl}
                                            isSticky={isSticky}
                                            searchPhrase={searchPhrase}
                                            searchUrl={links.search}
                                            pictureProfile={profile}
                                            setSearchActive={(value) => {
                                                this.closeCart();
                                                this.setState({
                                                    isSearchActive: value,
                                                    isSearchOpen: value
                                                });
                                            }}
                                            isActive={isSearchActive}
                                            searchPlaceholder={searchPlaceholder}
                                            leadInText={leadInText}
                                            setOverlayShown={(value) => this.setState({ isSearchOverlayShown: value })}
                                            searchRecommendationsProps={this.props.searchRecommendationsProps}
                                        />
                                    </div>

                                    <div className="Header__RightSide">
                                        {isShowSearchButtonVisible && (
                                            <div className="Header__SearchButton--Right">
                                                <HeaderSearchButton
                                                    isSearchOpen={isSearchOpen}
                                                    isVisible={isSearchOpen}
                                                    showButtonLabel={headerLabels.showLabels}
                                                    buttonLabel={headerLabels.search}
                                                    onSearchOpen={this.onSearchOpen}
                                                />
                                            </div>
                                        )}

                                        <HeaderActions
                                            isActionMenuOpen={isActionMenuOpen}
                                            isRxMenuOpen={isRxMenuOpen}
                                            headerLinks={links}
                                            userDetails={this.state.userDetails}
                                            setActionMenuOpen={(value) => {
                                                this.closeCart();
                                                this.setState({ isActionMenuOpen: value });
                                            }}
                                            setRxMenuOpen={(value) => {
                                                this.closeCart();
                                                this.setState({ isRxMenuOpen: value });
                                            }}
                                            togglePhysicalStoreMode={this.togglePhysicalStoreMode}
                                            isInPhysicalStoreMode={isInPhysicalStoreMode}
                                            cartItemPictureProfile={profile}
                                            isCartVisible={displayCart}
                                            actionMenuProfileLinks={this.props.model.actionMenuProfileLinks}
                                            rxMenuTitle={this.props.model.rxMenuHeader}
                                            rxMenuTitleActiveBankIdSession={this.props.model.rxMenuHeaderActiveBankIdSession}
                                            rxMenuLinks={this.props.model.rxMenuLinks}
                                            rxMenuArticlesLinks={this.props.model.rxMenuArticlesLinks}
                                            rxSessionCookieName={this.props.model.rxSessionCookieName}
                                            rxSessionValidityCookieName={this.props.model.rxSessionValidityCookieName}
                                            rxSessionExpirationTimeCookieName={this.props.model.rxSessionExpirationTimeCookieName}
                                            rxSessionRedIndicatorThresholdInMinutes={this.props.model.rxSessionRedIndicatorThresholdInMinutes}
                                            rxSessionValidityDefaultTimeInMinutes={this.props.model.rxSessionValidityDefaultTimeInMinutes}
                                            rxMenuMaintenance={this.props.model.rxMenuMaintenance}
                                            unreadMessagesBadgeUrl={this.props.model.unreadMessagesBadgeUrl}
                                            customerServiceLabel={this.props.model.customerServiceLabel}
                                            customerServicePhoneNumber={this.props.model.customerServicePhoneNumber}
                                            cartDrawerSettings={this.props.model.cartDrawerSettings}
                                            headerLabels={headerLabels}
                                            prescriptionListPageUrl={this.props.model.prescriptionListPageUrl}
                                            buyPrescriptionButtonText={this.props.model.buyPrescriptionButtonText}
                                        />
                                    </div>
                                </div>

                                <Navigation isVisible={isNavigationOpen} onClose={this.toggleNavigation} model={navigation} />

                                <div className="Header__TopPaddingBottom" />
                            </div>
                        </div>
                    )}

                    {isOnlyLogoVariant && (
                        <div className="Header__Top">
                            <a href="/" className="Header__Logo Header__Logo--Solo">
                                <img src={`/dist/assets/images/logo.svg?v=${websiteVersion}`} alt="Farmasiet Logo" width="230" height="32" />
                            </a>
                        </div>
                    )}

                    {isHomePageVariant && links.rxLandingPage && links.chatWithPharmacistLandingPage && (
                        <HeaderButtons rxLandingPageUrl={links.rxLandingPage} chatWithPharmacistLandingPageUrl={links.chatWithPharmacistLandingPage} />
                    )}
                    {isInPhysicalStoreMode && <div className="Header__PhysicalStoreModeLabel">{getTranslation(keys['header.physicalStoreMode'])}</div>}
                </header>
            </>
        );
    }

    private hideHeader = () => {
        this.setState({
            isActive: false
        });
    };

    private showHeaderBeforeCartUpdate = () => {
        if (this.state.isActive) {
            window.dispatchEvent(new CustomEvent(EventNames.ShowUpdatedCart));
        } else {
            this.setState({ isActive: true });
            (this.updateCartTimeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                window.dispatchEvent(new CustomEvent(EventNames.ShowUpdatedCart));
            }, 600);
        }
    };

    private toggleNavigation = () => {
        this.setState(
            (prevState) => {
                return { isNavigationOpen: !prevState.isNavigationOpen };
            },
            () => {
                if (this.state.isNavigationOpen) {
                    this.closeCart();
                    this.closeSearch();
                }
            }
        );
    };

    private onSearchOpen = () => {
        this.closeCart();

        if (!this.state.isSearchOpen && this.searchInputWrapperRef.current) {
            if (isMobile()) {
                this.setInputFocus();
            } else {
                setTimeout(() => {
                    this.setInputFocus();
                }, 1);
            }
        }

        if (isTablet()) {
            this.blockHandleScroll = true;
            (this.blockHandleScrollTimeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                this.blockHandleScroll = false;
            }, 100);
        }

        this.setState((prevState) => {
            return {
                isSearchOpen: !prevState.isSearchOpen,
                isSearchActive: !prevState.isSearchOpen,
                isNavigationOpen: false
            };
        });
    };

    private setInputFocus = () => {
        const input = this.getInput();
        if (input) {
            input.focus();
        }
    };

    private closeCart = () => {
        window.dispatchEvent(new CustomEvent(EventNames.ToggleCart, { detail: { open: false } }));
    };

    private closeSearch = () => {
        this.setState({ isSearchOpen: false, isSearchActive: false });
    };

    private closeAllMenuItems = () => {
        this.setState({ isSearchOpen: false, isSearchActive: false, isActionMenuOpen: false, isRxMenuOpen: false, isNavigationOpen: false });
    };

    private closeNavigation = () => {
        this.setState({ isNavigationOpen: false });
    };

    private hideAlertRibbon = () => {
        this.setState({ isAlertRibbonVisible: false, shouldBeAlertRibbonHidden: true });
    };

    private getHeaderClassNames = (): string => {
        const {
            props: {
                model: { variant, ribbon }
            },
            state: { isAlertRibbonVisible, shouldBeAlertRibbonHidden }
        } = this;

        return classNames(
            'Header',
            `Header--${HeaderVariant[variant]}`,
            this.state.isInPhysicalStoreMode && 'Header--PhysicalStoreMode',
            !shouldBeAlertRibbonHidden && isAlertRibbonVisible && 'Header--WithAlertRibbon',
            !!ribbon.items?.length && 'Header--WithRibbon'
        );
    };

    private getHeaderTopWrapperClassNames = (isSearchInitiallyOpen: boolean): string => {
        return classNames(
            'Header__TopWrapper',
            isSearchInitiallyOpen && 'Header__TopWrapper--WithSearch',
            !isSearchInitiallyOpen && 'Header__TopWrapper--WithAbsoluteSearch'
        );
    };

    private getHeaderTopClassNames = (isMobile: boolean): string => {
        const {
            props: {
                model: { variant }
            },
            state: { isSticky, isActive, isSearchActive, isSearchOverlayShown }
        } = this;
        const isCompactWithoutSticky = variant === HeaderVariant.CompactWithoutSticky || variant === HeaderVariant.CompactWithoutStickyAndSearch;

        return classNames(
            'Header__Top',
            isSticky && !isCompactWithoutSticky && 'Header__Top--Sticky',
            isActive && !isCompactWithoutSticky && 'Header__Top--Active',
            !isSticky && !isCompactWithoutSticky && 'Header__Top--WithSearch',
            isSearchActive && this.props.model.variant !== HeaderVariant.Search && 'Header__Top--ActiveSearch',
            isMobile && isSearchOverlayShown && 'Header__Top--Sticky Header__Top--Active'
        );
    };

    private togglePhysicalStoreMode = () => {
        const url = this.props.model.links.physicalStoreApi;
        const method = this.state.isInPhysicalStoreMode ? del(url) : post(url);
        method.then(() => {
            window.location.reload();
        });
    };

    private setUserDetails = (cart: CartViewModel, user: UserDetailsViewModel) => {
        if (user.isLoggedIn) {
            patchPhilLinks(this.props.philDomain);
            CookieAccessor.setCookie('userID', user.id, 15 * 60);
            CookieAccessor.setCookie('customerID', user.customerId, 15 * 60);
            if (cart.rxSessionExpirationTime) {
                CookieAccessor.setCookie('bankIDExpiration', cart.rxSessionExpirationTime, new Date(cart.rxSessionExpirationTime));
            }
        } else {
            CookieAccessor.deleteCookie('userID');
            CookieAccessor.deleteCookie('customerID');
        }
        SessionStorageAccessor.clearStorageOnUserIdChange(user.id);
        this.setState({ userDetails: user });
    };

    private getInput = (): HTMLInputElement | undefined => {
        if (this.searchInputWrapperRef.current) {
            const input = this.searchInputWrapperRef.current.getElementsByTagName('input');
            return input.length > 0 ? input[0] : undefined;
        }

        return undefined;
    };

    private handleClickOutside = (e: MouseEvent) => {
        if (this.headerRef.current && !this.headerRef.current.contains(e.target as Node) && this.state.isSearchOpen) {
            const input = this.getInput();
            if (!input || (input && !input.value)) {
                this.setState({ isSearchOpen: false, isSearchActive: false });
            }
        }
    };

    private handleScrollLock = ($event: CustomEventInit) => {
        if ($event.detail.active !== undefined) this.setState({ isScrollLockActive: $event.detail.active });

        if ($event.detail.forceOpen === true) this.setState({ isActive: true, isSticky: true });
    };

    private handleResize = () => {
        this.setState({ isMobile: isMobile(), isTablet: isTablet() });
    };

    private handleScroll = () => {
        if (this.blockHandleScroll) return;

        const isSearchPage = this.props.model.variant === HeaderVariant.Search;
        const ribbonHeight = this.ribbonRef.current?.clientHeight;
        const headerHeight = (this.headerRef.current?.clientHeight || 0) + (ribbonHeight || 0);
        const headerDistanceFromTop = ribbonHeight || 0;

        const { isScrollLockActive, isActive, isSearchActive, isSticky, isSearchOpen } = this.state;

        if (!isScrollLockActive && window.pageYOffset >= headerDistanceFromTop) {
            if (window.pageYOffset > this.lastOffsetTop) {
                if (window.pageYOffset > headerHeight && isSticky && isSearchOpen && !this.getInput()?.value) {
                    this.getInput()?.blur();
                    this.setState({ isSearchOpen: false, isSearchActive: false });
                }

                if ((window.pageYOffset > headerHeight || isSearchOpen || isSearchActive) && !this.state.isSticky) {
                    this.setState({
                        isSticky: true,
                        isActionMenuOpen: false,
                        isRxMenuOpen: false,
                        isActive: isSearchPage ? false : isSearchActive
                    });
                    if (isSearchPage && (isMobile() || isTablet())) {
                        this.setState({ isSearchOpen: false, isSearchActive: false });
                        this.getInput()?.blur();
                    }
                }

                if (isActive && !(isSearchActive && this.state.isMobile) && (!isSearchOpen || (isSearchPage && isSearchOpen))) {
                    this.setState({ isActive: false, isActionMenuOpen: false, isRxMenuOpen: false });
                    if (isSearchPage && (isMobile() || isTablet())) {
                        this.setState({ isSearchOpen: false, isSearchActive: false });
                        this.getInput()?.blur();
                    }
                }
            } else if (window.pageYOffset < this.lastOffsetTop) {
                if (window.pageYOffset <= headerDistanceFromTop && !isScrollLockActive && isSticky) {
                    this.setState({ isSticky: false });
                } else {
                    if (window.pageYOffset > headerHeight && !isActive) {
                        this.setState({ isActive: true });
                        if (isSearchPage && (isMobile() || isTablet())) {
                            this.setState({ isSearchOpen: true });
                        }
                    }
                }
            }

            this.lastOffsetTop = window.pageYOffset;
        } else if (!isScrollLockActive && window.pageYOffset < headerDistanceFromTop && (isSticky || isActive)) {
            this.setState({ isSticky: false, isActive: false });
        } else if (window.pageYOffset < 0 && (isSticky || isActive)) {
            this.setState({ isSticky: false, isActive: false });
        }
    };

    private showUpdatedCart(cart: CartViewModel) {
        const event = new CustomEvent(EventNames.CartValidated, {
            detail: { cart: cart }
        });
        window.dispatchEvent(event);

        setTimeout(() => {
            if (cart?.hasChangedAfterMerge) {
                showCartMergedModal(cart.hasRemovedRxItems);
            } else if (cart?.hasRemovedRxItems) {
                showRxSessionExpiredModal();
            }
        }, 10);
    }
}
